import React from 'react';
import { Box, CircularProgress, Typography, Backdrop } from '@mui/material';

export interface BglOverlayLoaderProps {
  /** Whether the loader is visible */
  open: boolean;
  /** Optional message to display below the spinner (default: "Loading...") */
  message?: string;
  /** Optional custom spinner size (default: 40) */
  size?: number;
  /** Optional custom color for the spinner */
  color?: string;
  /** Optional custom background color for the overlay */
  backgroundColor?: string;
  /** Optional z-index for the overlay (default: 1300) */
  zIndex?: number;
  /** Optional className for custom styling */
  className?: string;
  /** Optional callback when backdrop is clicked */
  onBackdropClick?: () => void;
  /** Whether to show backdrop click (default: false) */
  showBackdrop?: boolean;
}

export const BglOverlayLoader: React.FC<BglOverlayLoaderProps> = ({
  open,
  message = "Loading...",
  size = 40,
  color,
  backgroundColor = 'rgba(255, 255, 255, 0.4)',
  zIndex = 1300,
  className,
  onBackdropClick,
  showBackdrop = false,
}) => {
  if (!open) return null;

  return (
    <Box
      className={className}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor,
        zIndex,
        backdropFilter: 'blur(2px)',
      }}
      onClick={showBackdrop ? onBackdropClick : undefined}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: 3,
          border: 1,
          borderColor: 'divider',
        }}
        onClick={(e) => e.stopPropagation()} // Prevent click propagation to backdrop
      >
        <CircularProgress 
          size={size} 
          sx={{ 
            color: color || 'primary.main',
          }} 
        />
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            textAlign: 'center',
            maxWidth: 200,
            wordBreak: 'break-word',
          }}
        >
          {message}
        </Typography>
      </Box>
    </Box>
  );
};

// Alternative version using Material-UI Backdrop for better accessibility
export const BglBackdropLoader: React.FC<BglOverlayLoaderProps> = ({
  open,
  message = "Loading...",
  size = 40,
  color,
  backgroundColor = 'rgba(255, 255, 255, 0.8)',
  zIndex = 1300,
  className,
  onBackdropClick,
  showBackdrop = false,
}) => {
  return (
    <Backdrop
      open={open}
      onClick={showBackdrop ? onBackdropClick : undefined}
      sx={{
        zIndex,
        backgroundColor,
        backdropFilter: 'blur(2px)',
      }}
    >
      <Box
        className={className}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: 3,
          border: 1,
          borderColor: 'divider',
        }}
        onClick={(e) => e.stopPropagation()} // Prevent click propagation to backdrop
      >
        <CircularProgress 
          size={size} 
          sx={{ 
            color: color || 'primary.main',
          }} 
        />
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            textAlign: 'center',
            maxWidth: 200,
            wordBreak: 'break-word',
          }}
        >
          {message}
        </Typography>
      </Box>
    </Backdrop>
  );
}; 